import React, {useState, useEffect, useMemo} from 'react'
import GenericTable from '../../shared/generic-table'
import '../../../styles/control-center-styles.css'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import advanced from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import {toast} from 'react-toastify'
import {useAuthClient} from '../../../utils/use-auth-client'
import {getUserRoles} from '../../../utils/get-user-roles'

dayjs.extend(timezone)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advanced)

const DEFAULT_TIMEZONE = 'America/Chicago'
const formatDate = (dateStr, timezone = DEFAULT_TIMEZONE) => {
  return dayjs(dateStr).tz(timezone).format('MM/DD/YYYY HH:mmA z')
}

export const CountdownTimer = date => {
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    if (!date) return

    const calculateTimeLeft = () => {
      const now = dayjs()
      const expiry = dayjs(date)
      const diff = expiry.diff(now)

      if (diff <= 0) {
        setTimeLeft('Expired')
        return
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      )
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      setTimeLeft(
        `${days.toString().padStart(2, '0')}d : ${hours
          .toString()
          .padStart(2, '0')}h : ${minutes
          .toString()
          .padStart(2, '0')}m : ${seconds.toString().padStart(2, '0')}s`,
      )
    }

    calculateTimeLeft()
    const timer = setInterval(calculateTimeLeft, 1000)

    return () => clearInterval(timer)
  }, [date])

  if (!date) return 'N/A'

  return (
    <p
      className="formmated-date"
      style={{
        fontFamily: 'monospace',
        fontWeight: 'bold',
        borderRadius: '4px',
        cursor: 'default',
        whiteSpace: 'nowrap',
        margin: 0,
      }}
    >
      {timeLeft}
    </p>
  )
}

// Custom hook for debounced value
const useDebouncedValue = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => clearTimeout(timer)
  }, [value, delay])

  return debouncedValue
}

export const PendingLinkModal = ({link, open, onClose}) => {
  if (!open) return null

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <section className="appointment-info-modal">
        <div className="appointment-modal-header">
          <h2>Pending Link Details</h2>
          <button onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="appointment-info-content">
          {link && (
            <div className="appointment-details-container">
              <div className="appointment-details-grid">
                <div className="appointment-detail">
                  <span className="detail-label">Link</span>
                  <a
                    href={link.link}
                    target="_blank"
                    rel="noreferrer"
                    className="detail-value"
                    style={{color: '#2d8cff', textDecoration: 'none'}}
                  >
                    View Buy Now Link
                  </a>
                </div>
                <div className="appointment-detail">
                  <span className="detail-label">Client Email</span>
                  <span className="detail-value">
                    {link.client_email || 'N/A'}
                  </span>
                </div>
                <div className="appointment-detail">
                  <span className="detail-label">Order ID</span>
                  <span className="detail-value">{link.order_id || 'N/A'}</span>
                </div>
                <div className="appointment-detail">
                  <span className="detail-label">Created Date</span>
                  <span className="detail-value">
                    {formatDate(link.create_date)}
                  </span>
                </div>
                <div className="appointment-detail">
                  <span className="detail-label">Expiry Date</span>
                  <span className="detail-value">
                    {formatDate(link.link_expiry_datetime)}
                  </span>
                </div>
                <div className="appointment-detail">
                  <span className="detail-label">Order Total</span>
                  <span className="detail-value">
                    ${parseFloat(link.order_total).toFixed(2)}
                  </span>
                </div>
                {link.products_in_order && (
                  <div className="appointment-detail full-width">
                    <span className="detail-label">Products</span>
                    <div className="detail-value">
                      {Array.isArray(link.products_in_order.plan)
                        ? link.products_in_order.plan.join(', ')
                        : link.products_in_order.plan ||
                          'No products specified'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  )
}

const PendingBuyNowLinks = ({
  keapPartnerInfo,
  setDashboardLoading,
  dashboardLoading,
  title,
  userData,
}) => {
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(1)
  const [countPerPage] = useState(5)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedLink, setSelectedLink] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false,
  })

  // Consolidated filter state
  const [filters, setFilters] = useState({
    influencerId: '',
    companyName: '',
    clientEmail: '',
    linkId: '',
  })

  // Debounced filter values for API calls
  const debouncedInfluencerId = useDebouncedValue(filters.influencerId)
  const debouncedCompanyName = useDebouncedValue(filters.companyName)
  const debouncedClientEmail = useDebouncedValue(filters.clientEmail)
  const debouncedLinkId = useDebouncedValue(filters.linkId)

  const client = useAuthClient()

  // Check if user has InfluencerOps role - supports both Auth0 and Okta
  const userRoles = getUserRoles(userData)
  const isInfluencerOps = userRoles.includes('InfluencerOps')

  // Filter update handlers
  const updateFilter = (key, value) => {
    setFilters(prev => ({...prev, [key]: value}))
  }

  const clearAllFilters = () => {
    setFilters({
      influencerId: '',
      companyName: '',
      clientEmail: '',
      linkId: '',
    })
    setPage(1)
  }

  const baseColumns = useMemo(
    () => [
      {key: 'link_id', label: 'Link ID'},
      {key: 'company_name', label: 'Company Name'},
      {
        key: 'create_date',
        label: 'Created',
        render: date => (
          <p
            className="formmated-date"
            style={{whiteSpace: 'normal', margin: 0, fontSize: '12px'}}
          >
            {formatDate(date)}
          </p>
        ),
      },
      {
        key: 'client_email',
        label: 'Client Email',
        render: email => email || 'N/A',
      },
      {
        key: 'order_total',
        label: 'Order Total',
        render: total => `$${parseFloat(total).toFixed(2)}`,
      },
      {
        key: 'country_code',
        label: 'Country',
        render: countryCode => countryCode || '-',
      },
      {
        key: 'link_expiry_datetime',
        label: 'Expires',
        render: date =>
          date ? (
            <p
              className="formmated-date"
              style={{whiteSpace: 'nowrap', margin: 0}}
            >
              {formatDate(date)}
            </p>
          ) : (
            'N/A'
          ),
      },
      {
        key: 'link_expiry_datetime',
        label: 'Time Left',
        render: CountdownTimer,
      },
    ],
    [],
  )

  // Add Influencer column only for InfluencerOps users
  const influencerColumn = useMemo(
    () =>
      isInfluencerOps
        ? {
            key: 'influencer_name',
            label: 'Influencer',
            render: name => name || 'N/A',
          }
        : null,
    [isInfluencerOps],
  )

  const actionColumn = useMemo(
    () => ({
      key: 'actions',
      label: 'Actions',
      render: (_, row) => {
        const expiry = row && row.link_expiry_datetime
        const isExpired = expiry ? dayjs(expiry).isBefore(dayjs()) : false

        return (
          <div style={{display: 'flex', gap: '8px', whiteSpace: 'nowrap'}}>
            <button
              className="table-button"
              style={{whiteSpace: 'nowrap'}}
              onClick={e => {
                e.stopPropagation()
                setSelectedLink(row)
              }}
            >
              Details
            </button>

            {!isExpired && row && row.link && (
              <button
                className="table-button copy-link"
                style={{whiteSpace: 'nowrap'}}
                onClick={e => {
                  e.stopPropagation()
                  navigator.clipboard.writeText(row.link)
                }}
              >
                Copy Link
              </button>
            )}

            {/* <button
              className="table-button canceled"
              onClick={() => toast.info('This feature is not available yet')}
            >
              Cancel
            </button> */}
          </div>
        )
      },
    }),
    [],
  )

  // Memoize columns so the table does not re-render while typing in filters
  const columns = useMemo(() => {
    return influencerColumn
      ? [...baseColumns, influencerColumn, actionColumn]
      : [...baseColumns, actionColumn]
  }, [influencerColumn, baseColumns, actionColumn])

  const tableContent = useMemo(() => {
    if (!rows.length) return null

    return (
      <div className="control-center-table-wrapper">
        <GenericTable
          columns={columns}
          rows={rows}
          emptyMessage="No pending buy now links found."
          isLoading={isLoading}
          loadingMessage="Loading pending buy now links..."
          onRowClick={row => setSelectedLink(row)}
          pagination={{
            page: pagination.page,
            totalPages: pagination.totalPages,
            hasPrev: pagination.hasPrev,
            hasNext: pagination.hasNext,
            onPageChange: setPage,
          }}
        />
        <PendingLinkModal
          link={selectedLink}
          open={!!selectedLink}
          onClose={() => setSelectedLink(null)}
        />
      </div>
    )
  }, [columns, rows, isLoading, pagination, selectedLink, setPage])

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [
    debouncedInfluencerId,
    debouncedCompanyName,
    debouncedClientEmail,
    debouncedLinkId,
  ])

  useEffect(() => {
    const fetchPendingLinks = async () => {
      setIsLoading(true)
      setDashboardLoading(true)
      try {
        // Build URL with query parameters
        const params = new URLSearchParams({
          page: page.toString(),
          countPerPage: countPerPage.toString(),
        })

        if (debouncedInfluencerId)
          params.append('influencerId', debouncedInfluencerId)
        if (debouncedCompanyName)
          params.append('companyName', debouncedCompanyName)
        if (debouncedClientEmail)
          params.append('clientEmail', debouncedClientEmail)
        if (debouncedLinkId) params.append('linkId', debouncedLinkId)

        const url = `keappartner/fetchKeapPartnerPendingLink/${keapPartnerInfo.keapPartnerId}?${params}`
        const data = await client(url)

        if (data.success && data.data && Array.isArray(data.data.data)) {
          setRows(data.data.data)
          if (data.data.pagination) {
            setPagination({
              page: data.data.pagination.page,
              totalPages: data.data.pagination.totalPages,
              total: data.data.pagination.total,
              hasNext: data.data.pagination.hasNext,
              hasPrev: data.data.pagination.hasPrev,
            })
          }
        }
      } catch (error) {
        console.error('Error fetching pending links:', error)
        toast.error('Error fetching pending links')
        setRows([])
      } finally {
        setIsLoading(false)
        setDashboardLoading(false)
      }
    }

    fetchPendingLinks()
  }, [
    page,
    countPerPage,
    debouncedInfluencerId,
    debouncedCompanyName,
    debouncedClientEmail,
    debouncedLinkId,
    setDashboardLoading,
    client,
    keapPartnerInfo.keapPartnerId,
  ])

  return (
    <>
      <div style={{padding: 16}}>
        <h3
          className="control-center-item-header"
          style={{marginBottom: '8px'}}
        >
          {title}
        </h3>
        {rows.length > 0 && (
          <h4
            className="control-center-display-text"
            style={{marginBottom: '16px'}}
          >
            Displaying {page * countPerPage - countPerPage + 1}-
            {Math.min(page * countPerPage, pagination.total)} out of{' '}
            {pagination.total}
          </h4>
        )}
        <div
          style={{
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            display: 'flex',
            gap: '24px',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            border: '1px solid #e9e9e9',
          }}
        >
          {isInfluencerOps && (
            <div style={{flex: '1 1 auto', minWidth: '220px'}}>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  marginBottom: '6px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  color: '#666',
                  letterSpacing: '0.5px',
                }}
              >
                Influencer ID
              </label>
              <input
                type="text"
                placeholder="Enter influencer ID to filter"
                value={filters.influencerId}
                onChange={e => updateFilter('influencerId', e.target.value)}
                style={{
                  padding: '10px 12px',
                  borderRadius: '4px',
                  border: '1px solid #d4d4d4',
                  fontSize: '14px',
                  fontFamily: 'Open Sans, sans-serif',
                  width: '100%',
                  boxSizing: 'border-box',
                  backgroundColor: '#ffffff',
                  color: '#231f20',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => (e.target.style.borderColor = '#fa6000')}
                onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
              />
            </div>
          )}
          <div style={{flex: '1 1 auto', minWidth: '200px'}}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: '#666',
                letterSpacing: '0.5px',
              }}
            >
              Link ID
            </label>
            <input
              type="text"
              placeholder="Enter link ID to filter"
              value={filters.linkId}
              onChange={e => updateFilter('linkId', e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: '4px',
                border: '1px solid #d4d4d4',
                fontSize: '14px',
                fontFamily: 'Open Sans, sans-serif',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                color: '#231f20',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => (e.target.style.borderColor = '#fa6000')}
              onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
            />
          </div>
          <div style={{flex: '1 1 auto', minWidth: '250px'}}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: '#666',
                letterSpacing: '0.5px',
              }}
            >
              Company Name
            </label>
            <input
              type="text"
              placeholder="Enter company name to filter"
              value={filters.companyName}
              onChange={e => updateFilter('companyName', e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: '4px',
                border: '1px solid #d4d4d4',
                fontSize: '14px',
                fontFamily: 'Open Sans, sans-serif',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                color: '#231f20',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => (e.target.style.borderColor = '#fa6000')}
              onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
            />
          </div>
          <div style={{flex: '1 1 auto', minWidth: '250px'}}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: '#666',
                letterSpacing: '0.5px',
              }}
            >
              Client Email
            </label>
            <input
              type="text"
              placeholder="Enter client email to filter"
              value={filters.clientEmail}
              onChange={e => updateFilter('clientEmail', e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: '4px',
                border: '1px solid #d4d4d4',
                fontSize: '14px',
                fontFamily: 'Open Sans, sans-serif',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                color: '#231f20',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => (e.target.style.borderColor = '#fa6000')}
              onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
            />
          </div>
          <div style={{flex: '0 0 auto', minWidth: '120px'}}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: 'transparent',
                letterSpacing: '0.5px',
              }}
            >
              &nbsp;
            </label>
            <button
              onClick={clearAllFilters}
              style={{
                padding: '10px 16px',
                borderRadius: '4px',
                border: '1px solid #d4d4d4',
                fontSize: '14px',
                fontFamily: 'Open Sans, sans-serif',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                color: '#fa6000',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => {
                e.target.style.backgroundColor = '#fa6000'
                e.target.style.color = '#ffffff'
                e.target.style.borderColor = '#fa6000'
              }}
              onMouseLeave={e => {
                e.target.style.backgroundColor = '#ffffff'
                e.target.style.color = '#fa6000'
                e.target.style.borderColor = '#d4d4d4'
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
      {tableContent}
      {!(rows.length > 0) && !dashboardLoading && (
        <div style={{padding: 16}}>
          <div
            style={{
              textAlign: 'center',
              paddingTop: '60px',
              paddingBottom: '60px',
            }}
          >
            <h4
              style={{
                color: '#808080',
                fontSize: '16px',
                fontWeight: '600',
              }}
            >
              No Pending 1:1 links found
            </h4>
            <p style={{color: '#999', fontSize: '14px', marginTop: '8px'}}>
              Try adjusting your filters to see more results
            </p>
          </div>
        </div>
      )}
    </>
  )
}

export default PendingBuyNowLinks
